/**
 * The demo chrome's one pacing clamp.
 *
 * Two surfaces replay a REAL run's measurements back to a prospect — the
 * first-run rail's recorded live build (components/demo/firstRun/machine.ts)
 * and the recorded conversation replay (components/demo/askReplay/machine.ts).
 * Both face the same problem, so they share the same answer, once:
 *
 *   The numbers are honest but unwatchable. A real run's gaps are a 4ms cache
 *   probe next to a 90-second model call. Played back literally, the fast
 *   steps are invisible and the slow ones are dead air.
 *
 * So the measurements are turned into a READING PACE. Each is clamped into a
 * legible band, and if the clamped total still exceeds the surface's budget
 * the whole sequence is scaled proportionally — which preserves the SHAPE of
 * the run (which step was the long one) even when the wall clock can't be.
 *
 * The honesty line is drawn elsewhere and is not negotiable: what this
 * function returns is only ever used to TIME the reveal. Any duration a
 * prospect READS — the rail's per-step "8.4s", the replay's "Recorded run ·
 * 47s" — stays the server's own measurement and never passes through here.
 */

export interface PacingBand {
  /** Floor for one step. Below this a step can't be read at all. */
  minMs: number;
  /** Ceiling for one step. A genuinely slow step is reported by its LABEL; it
   *  does not also need to cost the prospect the silence. */
  maxMs: number;
  /** Whole-sequence budget. Exceeded ⇒ proportional compression. */
  budgetMs: number;
}

/**
 * Turn measured durations into per-step reveal delays.
 *
 * Returns one delay per input, in order: how long to wait BEFORE revealing
 * that step (or, for a streamed message, how long to spend streaming it).
 *
 * A non-finite measurement (NaN/Infinity from a malformed recording) is read
 * as 0 and therefore lands on `minMs` — a bad number costs one step its
 * relative weight, never the whole replay.
 */
export function paceDurations(measuredMs: readonly number[], band: PacingBand): number[] {
  if (measuredMs.length === 0) return [];
  const clamped = measuredMs.map((ms) =>
    Math.min(band.maxMs, Math.max(band.minMs, Number.isFinite(ms) ? ms : 0)),
  );
  const total = clamped.reduce((sum, ms) => sum + ms, 0);
  if (total <= band.budgetMs) return clamped;
  // Over budget: compress proportionally, so the SHAPE of the run (which step
  // was the long one) survives even when the wall clock cannot. The floor is
  // re-applied after scaling — compression must never push a step back below
  // the point where it can be read.
  const scale = band.budgetMs / total;
  return clamped.map((ms) => Math.max(band.minMs, Math.round(ms * scale)));
}
